import { Card, Table, Button } from 'antd';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ZorbaSelect } from '../../../../components/forms';
import { postRequest } from '../../../../services/requestService';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../../services/notificationsService';
import { isMobile } from '../../../../services/clientService';
import { getAllAgents } from '../../../../reducer/User/actions';
import { ADMIN_USERS, getAccessibleAgents, getTableColumns, daysOptions } from './constants';

import './index.scss';

export const BuyerAgentReport = () => {
  const { userDetails } = useSelector(({ AuthReducer }) => AuthReducer);
  const { agents } = useSelector(({ UserReducer }) => UserReducer);
  const dispatch = useDispatch();
  const options = getAccessibleAgents(userDetails.email, userDetails._id, agents, userDetails);
  const [selectValue, setSelectValue] = useState(null);
  const [selectedDays, setSelectedDays] = useState(30);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [tempValue, setTempValue] = useState(null);
  const isAdmin = ADMIN_USERS.includes(userDetails.email);
  const tableScroll = {
    scrollToFirstRowOnChange: true,
    x: isMobile() ? true : 'max-content',
  };

  const pagination = {
    position: ['bottomRight'],
    showTotal: (totalItems, range) =>
      `${range[0]}-${range[1]} of ${totalItems} items`,
    defaultPageSize: 50,
  };

  useEffect(() => {
    const loadAgents = async () => {
      setAgentsLoading(true);
      try {
        await dispatch(getAllAgents());
      } catch (error) {
        showErrorToast('Failed to load agents!');
      } finally {
        setAgentsLoading(false);
      }
    };
    loadAgents();
  }, [dispatch]);

  useEffect(() => {
    if (options?.length > 0 && !selectValue) {
      setSelectValue(options[0].value);
    }
  }, [options, selectValue]);

  useEffect(() => {
    const fetchReport = async (agentId, daysSince) => {
      if (agentId === null || agentsLoading) return; // Don't fetch if no agent selected or agents still loading
      
      setIsLoading(true);
      try {
        const response = await postRequest(
          '/backoffice/buyers-report/get-smb-report',
          { agentId: agentId || null, daysSince }, // Send null for "All Agents" case
        );
        setData(response?.data?.data || []);
      } catch (error) {
        showErrorToast('Fetching report failed!');
      }
      setIsLoading(false);
    };
    fetchReport(selectValue, selectedDays);
  }, [selectValue, selectedDays, agentsLoading]);


  const handleAssociatedBuyerAgentChange = async (
    index,
    userId,
    associatedBuyerAgentId,
  ) => {
    setIsLoading(true);
    try {
      setTempValue(associatedBuyerAgentId);
      const response = await postRequest(
        `/backoffice/contacts-report/set-associated-buyer-agent/${userId}`,
        { associatedBuyerAgentId },
      );
      const newData = [...data];

      newData[index].contactBuyerAgentId = response.data.associatedBuyerAgentId;
      newData[index].contactBuyerAgent = response.data.associatedBuyerAgentName;
      setData(newData);
      setEditingKey(null);

      if (associatedBuyerAgentId === null) {
        showSuccessToast('Buyer agent successfully unassigned!');
      } else {
        showSuccessToast('Buyer agent successfully associated!');
      }

      // Refresh the data to ensure consistency
      const refreshResponse = await postRequest(
        '/backoffice/buyers-report/get-report',
        { agentId: selectValue || null, daysSince: selectedDays },
      );
      setData(refreshResponse?.data?.data || []);
    } catch (error) {
      console.log(error)
      showErrorToast('Setting associated buyer agent failed!');
    }
    setIsLoading(false);
  };

  const handleClearAgent = () => {
    setSelectValue(null);
    setData([]);
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {data.length > 0 && (
            <span style={{
              background: '#f0f0f0',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              color: '#666'
            }}>
              {data.length} contact{data.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      }
      className="buyer-agent-report"
    >
      <div className="filters-container">
        <div className="agent-select-container">
          <ZorbaSelect
            options={options}
            value={selectValue}
            onChange={(e) => setSelectValue(e)}
            className="agent-select"
            placeholder={agentsLoading ? "Loading agents..." : "Select an agent"}
            disabled={agentsLoading}
          />
        </div>
        <div className="days-select-container">
          <ZorbaSelect
            options={daysOptions}
            value={selectedDays}
            onChange={(e) => setSelectedDays(e)}
            className="days-select"
            disabled={isLoading || agentsLoading}
          />
        </div>
        <div className="clear-agent-container">
          <Button
            onClick={handleClearAgent}
            disabled={!selectValue || isLoading || agentsLoading}
            className="clear-agent-button"
          >
            Clear Agent
          </Button>
        </div>
      </div>
      <Table
        columns={getTableColumns({
          isAdmin,
          editingKey,
          setEditingKey,
          handleAssociatedBuyerAgentChange,
          tempValue,
          agents,
        })}
        dataSource={data}
        loading={isLoading || agentsLoading}
        scroll={tableScroll}
        pagination={pagination}
      />
    </Card>
  );
};